// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter, JSX } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import type { Size } from '../shared'

/**
 *
 *
 * @see https://quartz.se.com/build/components/nav-list-item
 */
@Component({
  tag: 'qds-nav-list-item',
  shadow: { delegatesFocus: true },
  styleUrl: 'nav-list-item.css',
})
export class NavListItem implements ComponentInterface {
  /**
   * Displays the navigation list item in a collapsed state without its text.
   * The text will be displayed in a tooltip instead.
   *
   * This property has no effect if the navigation list item does not have an
   * icon set.
   */
  @Prop() public readonly collapsed: boolean = false

  /**
   * Prevents the navigation list item from being interacted with: it cannot be
   * selected or focused.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The navigation list items's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Whether or not the navigation list item is selected.
   */
  @Prop() public readonly selected: boolean = false

  /**
   * Text to display for the subtext.
   */
  @Prop() public readonly subtext?: string

  /**
   * Primary text to display.
   */
  @Prop() public readonly text!: string

  /**
   * Emitted when the navigation list item loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the navigation list item gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLQdsNavListItemElement

  @State() private tabIndex?: number

  private inheritedAttributes: Attributes = {}

  private li?: HTMLLIElement

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #shouldCollapse(): boolean {
    return this.collapsed && this.iconName !== undefined && this.iconName !== ''
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.disabled) event.stopImmediatePropagation()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  public componentWillLoad(): void {
    this.inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      <Host>
        <li
          class={{
            'qds-nav-list-item': true,
            'qds-disabled': this.disabled,
            'qds-selected': this.selected,
          }}
          ref={this.#liRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.inheritedAttributes}
        >
          <button
            class={{
              'qds-button': true,
              'qds-selected': this.selected,
            }}
            data-size={this.#computedSize}
            disabled={this.disabled}
            onBlur={this.#onBlur}
            onFocus={this.#onFocus}
            tabIndex={this.disabled ? undefined : this.tabIndex}
            type="button"
          >
            {this.iconName !== undefined && this.iconName !== '' && (
              <qds-icon
                aria-hidden="true"
                class="qds-icon"
                library={this.iconLibrary}
                name={this.iconName}
              />
            )}
            {!this.#shouldCollapse && this.text && this.renderText()}
          </button>
        </li>
        {this.#shouldCollapse && this.text && (
          <qds-tooltip placement="right" ref={this.#tooltipRef}>
            {this.renderText()}
          </qds-tooltip>
        )}
      </Host>
    )
  }

  private renderText(): JSX.Element {
    return (
      <div class="qds-texts">
        <div class="qds-text">{this.text}</div>
        {this.subtext !== undefined && this.subtext !== '' && (
          <div class="qds-subtext">{this.subtext}</div>
        )}
      </div>
    )
  }

  readonly #liRef = (li?: HTMLLIElement): void => {
    this.li = li
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.li
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }
}
